import { QueryClient } from "@tanstack/react-query";
import { createAsyncNode, driver } from "@leagueplatform/masonry";
import { DEFAULT_LOCALE, getCurrentLocale } from "@leagueplatform/locales";

/**
 * Reproduces the query key the SDK's `NodeResolver` builds for an async root
 * node, which `@leagueplatform/masonry-engine` keeps internal. A drift in that
 * key costs the warm cache and nothing else — the node then loads on mount.
 */
const ASYNC_NODE_QUERY_KEY = "MASONRY_ENGINE_GET_NODE_ASYNC_SELF";

type GetterParams = Parameters<typeof driver.getAsyncNodeData>[0];

type PrefetchedNode = {
  properties?: { image?: unknown };
  sections?: Record<string, PrefetchedNode[]>;
};

const pendingPrefetches = new Map<string, Promise<void>>();

const preloadNodeImages = (node: PrefetchedNode) => {
  const { image } = node.properties ?? {};

  if (typeof image === "string" && image) {
    const preloader = new Image();
    preloader.src = image;
  }

  Object.values(node.sections ?? {}).forEach((sectionNodes) =>
    sectionNodes.forEach(preloadNodeImages)
  );
};

export const prefetchMasonryNode = (
  queryClient: QueryClient,
  rootNodeId: string
) => {
  const { asyncConfig } = createAsyncNode({ id: rootNodeId });

  if (!asyncConfig) return Promise.resolve();

  const getterParams = asyncConfig.getterParams as GetterParams;

  const prefetch = queryClient
    .fetchQuery(
      [
        ASYNC_NODE_QUERY_KEY,
        rootNodeId,
        getterParams,
        {},
        getCurrentLocale(DEFAULT_LOCALE),
      ],
      () => driver.getAsyncNodeData(getterParams, undefined)
    )
    .then((node) => {
      preloadNodeImages(node as PrefetchedNode);
    })
    .catch(() => undefined)
    .finally(() => {
      pendingPrefetches.delete(rootNodeId);
    });

  pendingPrefetches.set(rootNodeId, prefetch);

  return prefetch;
};

export const awaitMasonryNodePrefetch = (rootNodeId: string) =>
  pendingPrefetches.get(rootNodeId) ?? Promise.resolve();
